import React, { useEffect, useState } from "react";
import api from "../../../services/api";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { usePermission } from "../../../hooks/usePermissions";
import styles from "./AccessTypes.module.css";
import FullscreenModal from "../../common/FullscreenModal/FullscreenModal";
import AddAccessTypes from "./AddAccessTypes";

const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

const AccessTypes = () => {
    const navigate = useNavigate();

    const [accessTypes, setAccessTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTypeId, setSelectedTypeId] = useState(null);
    const itemsPerPage = 10;
    const { hasPermission } = usePermission();

    const fetchAccessTypes = async () => {
        setLoading(true);
        try {
            const response = await api.get("/access-types");
            setAccessTypes(response.data || []);
        } catch (error) {
            console.error("Error fetching access types:", error);
            toast.error("Failed to load access types");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this Access Type?"))
            return;

        try {
            await api.delete(`/access-types/${id}`);
            toast.success("Access Type deleted successfully");
            fetchAccessTypes();
        } catch (error) {
            const msg = error?.message || "Cannot delete: This access type is linked.";
            toast.error(msg);
        }
    };

    useEffect(() => {
        fetchAccessTypes();
    }, []);

    return (
        <div className={styles.container}>
            <ToastContainer position="bottom-right" autoClose={2000} />

            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.pageTitle}>Access Types</h1>
                    <p className={styles.pageSubtitle}>Manage and categorize different system access categories</p>
                </div>

                {hasPermission("CREATE_ACCESSCONTROL") && (
                    <button
                        className={styles.addBtn}
                        onClick={() => {
                            setSelectedTypeId(null);
                            setIsModalOpen(true);
                        }}
                    >
                        + Add Type
                    </button>
                )}
            </div>

            <div className={styles.tableWrapper}>
                {(() => {
                    const totalPages = Math.max(1, Math.ceil(accessTypes.length / itemsPerPage));
                    const paginatedTypes = accessTypes.slice(
                        (currentPage - 1) * itemsPerPage,
                        currentPage * itemsPerPage
                    );

                    return (
                        <>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.w80px}>ID</th>
                            <th>Access Type</th>
                            <th>Rights Type</th>
                            <th>Platform</th>
                            <th className={styles.w120px}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} className={styles.emptyState}>Loading access types...</td>
                            </tr>
                        ) : paginatedTypes.length > 0 ? (
                            paginatedTypes.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.id}</td>
                                    <td className={styles.fontMedium}>{row.access_types}</td>
                                    <td>{row.rights_type}</td>
                                    <td>{row.access_portal || "—"}</td>
                                    <td>
                                        <div className={styles.actionsCell}>
                                            {hasPermission("MODIFY_ACCESSCONTROL") && (
                                                <button
                                                    className={`${styles.iconBtn} ${styles.edit}`}
                                                    onClick={() => {
                                                        setSelectedTypeId(row.id);
                                                        setIsModalOpen(true);
                                                    }}
                                                    title="Edit"
                                                >
                                                    <EditIcon />
                                                </button>
                                            )}

                                            {hasPermission("DELETE_ACCESSCONTROL") && (
                                                <button
                                                    className={`${styles.iconBtn} ${styles.delete}`}
                                                    onClick={() => handleDelete(row.id)}
                                                    title="Delete"
                                                >
                                                    <TrashIcon />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className={styles.emptyState}>No access types found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {!loading && accessTypes.length > 0 && (
                    <div className={styles.tableFooter}>
                        <span>
                            Showing {paginatedTypes.length} of {accessTypes.length} types
                        </span>

                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    type="button"
                                    className={styles.pageBtn}
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage((prev) => prev - 1)}
                                >
                                    {"<"}
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                    if (
                                        page === 1 ||
                                        page === totalPages ||
                                        (page >= currentPage - 1 && page <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                type="button"
                                                key={page}
                                                className={`${styles.pageBtn} ${currentPage === page ? styles.active : ""}`}
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </button>
                                        );
                                    }

                                    if (page === currentPage - 2 || page === currentPage + 2) {
                                        return (
                                            <span key={page} className={styles.paginationEllipsis}>
                                                ...
                                            </span>
                                        );
                                    }

                                    return null;
                                })}

                                <button
                                    type="button"
                                    className={styles.pageBtn}
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((prev) => prev + 1)}
                                >
                                    {">"}
                                </button>
                            </div>
                        )}
                    </div>
                )}
                </>
                );
            })()}
            </div>
            
            {isModalOpen && (
                <FullscreenModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={selectedTypeId ? "Edit Access Type" : "Create Access Type"}
                    maxWidth="600px"
                >
                    <AddAccessTypes
                        typeId={selectedTypeId}
                        onSubmitted={() => {
                            setIsModalOpen(false);
                            fetchAccessTypes();
                        }}
                        onCancel={() => setIsModalOpen(false)}
                    />
                </FullscreenModal>
            )}
        </div>
    );
};

export default AccessTypes;